/*
 * Copyright (c) Castle Point Learning Systems Corporation, 2013-2023, all rights reserved.
 * NOTICE: Dissemination of the information and/or materials contained in this file is strictly
 * forbidden unless prior written permission is obtained from Castle Point Learning Systems Corporation.
 * See LICENSE.txt (part of this package) for the full notice.
 */

import { useCallback, useContext } from 'react';
import { EvaluationContext } from '../context/EvaluationContextProvider';
import { AssistElement, EvaluationLine } from '../../../types/model';
import { calculateParts, createEquality, lastPart, localId, phonyEquality } from '../utils/evaluationUtils';
import { MathNodeName, MathStructure } from 'semantic-math-editor';

export const useEvaluationLineCreation = (lineId: string, assist: AssistElement[]) => {
    const { dispatchEvaluation } = useContext(EvaluationContext);

    return useCallback(
        (inPlace: boolean = false) => {
            dispatchEvaluation((prevState) => {
                const prevLine = prevState.lines.find((l) => l.id === lineId);
                const last = lastPart(prevLine);
                const parts = calculateParts(prevLine.tree);
                const partIndexesWithErrors = assist ? assist.map((a) => a.partIndex) : [];
                let tree: MathStructure, unableToCreate: boolean;

                if (parts.length === 2) {
                    unableToCreate = [0, 1].some((i) => partIndexesWithErrors.includes(i));
                    tree = createEquality(parts[0], parts[1]);
                } else {
                    unableToCreate = partIndexesWithErrors.includes(parts.indexOf(last));
                    tree = phonyEquality(last);
                }

                if (last.name === MathNodeName.placeholder || unableToCreate) {
                    return prevState;
                }

                prevLine.editor.blur();
                const line: EvaluationLine = { id: localId(), tree, firstEqualityX: 0, lastEqualityX: 0 };
                const index = prevState.lines.findIndex((l) => l.id === lineId);
                return {
                    ...prevState,
                    focused: false,
                    lines: inPlace
                        ? prevState.lines
                              .slice(0, index + 1)
                              .concat(line)
                              .concat(prevState.lines.slice(index + 1))
                        : prevState.lines.concat(line),
                    activeLine: { lineId: line.id, activePartIndex: 1, partsCount: 2 },
                };
            });
        },
        [dispatchEvaluation, lineId, assist]
    );
};
